import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { emitToast } from '../../context/toast';

const FREQUENCIES = [
  { value: 'diaria', label: 'Diaria' },
  { value: 'cada_3_dias', label: 'Cada 3 días' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'quincenal', label: 'Quincenal' },
  { value: 'mensual', label: 'Mensual' },
];

export default function TrainingCoachClientConfig({ onBack }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/training/coach/client-config');
      setConfig(r.data?.data || null);
    } catch {
      emitToast({ type: 'error', title: 'Error', message: 'No se pudo cargar la configuración' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaveNotice('');
    try {
      const r = await api.put('/training/coach/client-config', config);
      setConfig(r.data?.data);
      setSaveNotice('Configuración guardada correctamente.');
      emitToast({
        type: 'success',
        title: 'Guardado',
        message: 'Fotos y medidas actualizadas.',
      });
    } catch (e) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: e.response?.data?.error || 'No se pudo guardar la configuración',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <div className="dashboard-main-view training-coach-view training-light-island surface-readable d28d-admin-surface p-4">
        <p className="text-sm text-stone-600">Cargando…</p>
      </div>
    );
  }

  const toggleField = (fieldKey) => {
    setConfig((prev) => ({
      ...prev,
      measurements: {
        ...prev.measurements,
        fields: prev.measurements.fields.map((f) => (
          f.key === fieldKey ? { ...f, enabled: !f.enabled } : f
        )),
      },
    }));
  };

  return (
    <div className="dashboard-main-view training-coach-view training-light-island surface-readable d28d-admin-surface max-w-3xl mx-auto p-5 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900">Fotos y medidas de clientes</h2>
          <p className="text-sm text-stone-600 mt-1">
            Si el cliente ya usa FOOD Plan, puede reutilizar sus datos (sin duplicar trabajo).
          </p>
        </div>
        {onBack ? (
          <button type="button" className="btn-secondary text-sm" onClick={onBack}>Volver</button>
        ) : null}
      </div>

      {saveNotice ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm p-3 rounded-lg" role="status">
          {saveNotice}
        </div>
      ) : null}

      <section className="card ui-light-panel p-4 space-y-3">
        <h3 className="font-semibold text-stone-900">Fotos de progreso</h3>
        <p className="text-sm text-stone-600">
          Cuántas fotos por toma y cada cuánto debe subirlas el entrenado. El historial no tiene tope de 8 fotos.
        </p>
        <label className="block text-sm text-stone-800">
          Fotos por toma
          <select
            className="input max-w-xs mt-1 block"
            value={config.photos?.max_photos || 4}
            onChange={(e) => setConfig((p) => ({
              ...p,
              photos: { ...p.photos, max_photos: Number(e.target.value) },
            }))}
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-stone-800">
          Cada cuánto
          <select
            className="input max-w-xs mt-1 block"
            value={config.photos?.frequency || 'semanal'}
            onChange={(e) => setConfig((p) => ({
              ...p,
              photos: { ...p.photos, frequency: e.target.value },
            }))}
          >
            {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {(config.photos?.positions || []).map((pos, idx) => (
            <label key={pos.key} className="flex items-center gap-2 text-sm text-stone-800">
              <input
                type="checkbox"
                checked={pos.enabled !== false}
                onChange={() => setConfig((p) => ({
                  ...p,
                  photos: {
                    ...p.photos,
                    positions: p.photos.positions.map((x, i) => (
                      i === idx ? { ...x, enabled: x.enabled === false } : x
                    )),
                  },
                }))}
              />
              {pos.label}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-800">
          <input
            type="checkbox"
            checked={Boolean(config.notifications?.photos_remind)}
            onChange={(e) => setConfig((p) => ({
              ...p,
              notifications: {
                ...(p.notifications || {}),
                photos_remind: e.target.checked,
              },
            }))}
          />
          Avisar en la campanita cuando el entrenado no suba las fotos a tiempo
        </label>
      </section>

      <section className="card ui-light-panel p-4 space-y-3">
        <h3 className="font-semibold text-stone-900">Medidas</h3>
        <label className="flex items-center gap-2 text-sm text-stone-800">
          <input
            type="checkbox"
            checked={config.measurements?.prefer_food_source !== false}
            onChange={(e) => setConfig((p) => ({
              ...p,
              measurements: { ...p.measurements, prefer_food_source: e.target.checked },
              photos: { ...p.photos, prefer_food_source: e.target.checked },
            }))}
          />
          Priorizar datos existentes en FOOD Plan (recomendado)
        </label>
        <select
          className="input max-w-xs"
          value={config.measurements?.frequency || 'semanal'}
          onChange={(e) => setConfig((p) => ({
            ...p,
            measurements: { ...p.measurements, frequency: e.target.value },
          }))}
        >
          {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          {(config.measurements?.fields || []).map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm text-stone-800">
              <input type="checkbox" checked={f.enabled} onChange={() => toggleField(f.key)} />
              {f.label}
            </label>
          ))}
        </div>
      </section>

      <section className="card ui-light-panel p-4 space-y-2">
        <h3 className="font-semibold text-stone-900">Avisos de semáforo (adherencia)</h3>
        <label className="flex items-center gap-2 text-sm text-stone-800">
          <input
            type="checkbox"
            checked={Boolean(config.notifications?.traffic_light_notify)}
            onChange={(e) => setConfig((p) => ({
              ...p,
              notifications: {
                ...(p.notifications || {}),
                traffic_light_notify: e.target.checked,
              },
            }))}
          />
          Enviar avisos in_app/email de semáforo a mis clientes (opt-in)
        </label>
        <p className="text-xs text-stone-500">
          Desactivado por defecto. Requiere job TRAINING_TRAFFIC_LIGHT_JOB en el servidor.
        </p>
      </section>

      <section className="card ui-light-panel p-4 space-y-2">
        <h3 className="font-semibold text-stone-900">Video tutorial de medidas</h3>
        <input
          className="input w-full"
          placeholder="URL YouTube/Vimeo"
          value={config.measurements_video_url || ''}
          onChange={(e) => setConfig((p) => ({ ...p, measurements_video_url: e.target.value }))}
        />
      </section>

      <button type="button" className="btn-primary" disabled={saving} onClick={save}>
        {saving ? 'Guardando…' : 'Guardar configuración'}
      </button>
    </div>
  );
}
